import {
  breakpointSizes,
  Checkbox,
  getBreakpointDown,
  getBreakpointUp,
  getFontCss,
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import _ from 'lodash'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectFilteredPeopleInvitedToOrg,
  selectNumberOfInvitedToOrgFromState,
} from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.selectors'
import { PeopleMembersAnalyticsEventProperties } from 'src/app/PeopleMembers/PeopleMembers'
import { createGetOrganizationMembersInitAction } from 'src/app/PeopleMembers/redux/peopleMembers.actions'
import {
  selectFilteredPeopleMembers,
  selectNumberOfMembersFromState,
} from 'src/app/PeopleMembers/redux/peopleMembers.selectors'
import Box from 'src/components/Box/Box'
import FilterSection from 'src/components/FilterSection/FilterSection'
import KeywordFilter from 'src/components/KeywordFilter/KeywordFilter'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import {
  PeopleFiltersBody,
  PeopleInfiniteScrollProps,
} from 'src/components/PeopleBoxAndLinks/PeopleBoxAndLinks'
import PeopleInvitedToOrgList from 'src/components/PeopleInvitedToOrgList/PeopleInvitedToOrgList'
import PeopleInvitedToOrgListSmall from 'src/components/PeopleInvitedToOrgList/PeopleInvitedToOrgListSmall'
import PeopleMembersList from 'src/components/PeopleMembersList/PeopleMembersList'
import PeopleMembersListSmall from 'src/components/PeopleMembersList/PeopleMembersListSmall'
import TestId from 'src/components/TestId/TestId'
import { OrganizationRoles } from 'src/enums'
import {
  useBackgroundDispatch,
  useDebounced,
  useWindowScrollBottomHandler,
} from 'src/hooks'
import Trns, {
  translateSingleString,
  useTranslate,
} from 'src/localization/Trns'
import { peopleInvitedToOrgUrl, peopleMembersUrl } from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { SearchQueryParams } from 'src/redux/createRootReducer'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectIsLeftFlyoutOpen,
  selectIsRightFlyoutOpen,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { selectBrandColorCta } from 'src/redux/selectors/session.selectors'
import { setEquality } from 'src/utils/set.utils'
import styled, { css } from 'styled-components'

interface PeopleProps {
  peopleInfiniteScrollProps: PeopleInfiniteScrollProps
  loading?: boolean
  showFilters?: boolean
  stateQueryParams: SearchQueryParams
  setStateQueryParams: (SearchQueryParams: SearchQueryParams) => void
  keywordFilter?: string
  setKeywordFilter?: (keyword: string) => void
  organizationRolesFilter?: Set<string>
  setOrganizationRoleFilter?: (organizationRoles: Set<string>) => void
  handleOrganizationRoleSelection?: (role: string) => void
  hideDeactivatedUsers?: boolean
  setHideDeactivatedUsers?: (hideDeactivatedUsers: boolean) => void
  setSort: (sortField: string) => void
  sortBy?: string
  sortDescending?: boolean
  initialLoad: boolean
  setInitialLoad: (initialLoad: boolean) => void
  setSelectedFilters?: (
    selectedFilters: PeopleMembersAnalyticsEventProperties,
  ) => void
}

const PeopleListDiv = styled.div`
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
`

const PeopleBox: React.FC<PeopleProps> = (props) => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const numberOfOrgMembers = useSelector(selectNumberOfMembersFromState)
  const numberOfInvitedPeople = useSelector(selectNumberOfInvitedToOrgFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const isRightFlyoutOpen = useSelector(selectIsRightFlyoutOpen)
  const isLeftFlyoutOpen = useSelector(selectIsLeftFlyoutOpen)
  const memberListModels = useSelector(selectFilteredPeopleMembers)
  const invitedListModels = useSelector(selectFilteredPeopleInvitedToOrg)
  const queryParams = useSelector(selectQueryParams)
  const brandColorCta = useSelector(selectBrandColorCta)
  const currentOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: currentOrganization?.id,
  })

  const translate = useTranslate()
  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()

  // prevents loading indicator from showing when page is in the background
  const [loading, setLoading] = useState<boolean>(!!props.loading)
  const [pushNewSearchState, setPushNewSearchState] = useState<boolean>(false)
  const [initialRender, setInitialRender] = useState<boolean>(true)
  const [roleFilterSelected, setRoleFilterSelected] = useState<boolean>(false)

  useEffect(() => {
    if (isRightFlyoutOpen) {
      setLoading(false)
    } else {
      setLoading(!!props.loading)
    }
  }, [isRightFlyoutOpen, props.loading])

  useEffect(() => {
    if (!isRightFlyoutOpen) {
      props.setStateQueryParams(queryParams)
    }
  }, [queryString.stringify(queryParams)])

  const peopleList = (): React.ReactElement => {
    switch (calculatedBackgroundLocation.pathname) {
      case peopleMembersUrl():
      default:
        return (
          <div>
            <PeopleListDiv
              css={css`
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  display: none;
                `)}
              `}
            >
              <PeopleMembersList
                loading={loading}
                people={Object.values(memberListModels)}
                sortableProps={{
                  setSort: props.setSort,
                  sortBy: props.sortBy,
                  sortDescending: props.sortDescending,
                }}
              />
            </PeopleListDiv>
            <PeopleListDiv
              css={css`
                ${getBreakpointUp(breakpointSizes.SM)(css`
                  display: none;
                `)}
              `}
            >
              <PeopleMembersListSmall
                loading={loading}
                people={Object.values(memberListModels)}
                sortableProps={{
                  setSort: props.setSort,
                  sortBy: props.sortBy,
                  sortDescending: props.sortDescending,
                }}
              />
            </PeopleListDiv>
          </div>
        )
      case peopleInvitedToOrgUrl():
        return (
          <div>
            <PeopleListDiv
              css={css`
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  display: none;
                `)}
              `}
            >
              <PeopleInvitedToOrgList
                loading={loading}
                invites={Object.values(invitedListModels)}
                sortableProps={{
                  setSort: props.setSort,
                  sortBy: props.sortBy,
                  sortDescending: props.sortDescending,
                }}
              />
            </PeopleListDiv>
            <PeopleListDiv
              css={css`
                ${getBreakpointUp(breakpointSizes.SM)(css`
                  display: none;
                `)}
              `}
            >
              <PeopleInvitedToOrgListSmall
                loading={loading}
                invites={Object.values(invitedListModels)}
                sortableProps={{
                  setSort: props.setSort,
                  sortBy: props.sortBy,
                  sortDescending: props.sortDescending,
                }}
              />
            </PeopleListDiv>
          </div>
        )
    }
  }

  const sectionCount = (): React.ReactElement => {
    switch (calculatedBackgroundLocation.pathname) {
      case peopleMembersUrl():
      default:
        return numberOfOrgMembers === 1 ? (
          isFiltered ? (
            <Trns
              source={'${numberOfResults} Result'}
              expressions={{
                numberOfResults: numberOfOrgMembers.toString(),
              }}
            />
          ) : (
            <Trns
              source={'${numberOfOrgMembers} Active Org Member'}
              expressions={{
                numberOfOrgMembers: numberOfOrgMembers.toString(),
              }}
            />
          )
        ) : isFiltered ? (
          <Trns
            source={'${numberOfResults} Results'}
            expressions={{
              numberOfResults: numberOfOrgMembers.toString(),
            }}
          />
        ) : loading ? (
          <Processing className={'processing'} />
        ) : (
          <Trns
            source={
              props.hideDeactivatedUsers
                ? '${numberOfOrgMembers} Active Org Members'
                : '${numberOfOrgMembers} Org Members'
            }
            expressions={{
              numberOfOrgMembers: numberOfOrgMembers.toString(),
            }}
          />
        )
      case peopleInvitedToOrgUrl():
        return numberOfInvitedPeople === 1 ? (
          isFiltered ? (
            <Trns
              source={'${numberOfResults} Result'}
              expressions={{
                numberOfResults: numberOfInvitedPeople.toString(),
              }}
            />
          ) : (
            <Trns
              source={'${numInvited} Invited Person'}
              expressions={{
                numInvited: numberOfInvitedPeople.toString(),
              }}
            />
          )
        ) : isFiltered ? (
          <Trns
            source={'${numberOfResults} Results'}
            expressions={{
              numberOfResults: numberOfInvitedPeople.toString(),
            }}
          />
        ) : (
          <Trns
            source={'${numInvited} Invited People'}
            expressions={{
              numInvited: numberOfInvitedPeople.toString(),
            }}
          />
        )
    }
  }

  const handleSelection = (): void => {
    // call init action to set loading state immediately
    dispatch(createGetOrganizationMembersInitAction())
    pushUrlStateDebounce()
  }

  const [isFiltered, setIsFiltered] = useState(false)

  const filtersBody: PeopleFiltersBody = {
    search: props.keywordFilter ? `${props.keywordFilter}` : undefined,
    organizationRoles: props.organizationRolesFilter
      ? (Array.from(props.organizationRolesFilter) as OrganizationRoles[])
      : undefined,
    showDeactivatedUsers: !props.hideDeactivatedUsers,
    sortField: props.sortBy ? props.sortBy : undefined,
    sortDirection: props.sortDescending === false ? 'ASC' : 'DESC',
  }

  const { debouncedF } = useDebounced((): void => {
    if (!isRightFlyoutOpen && !isLeftFlyoutOpen) {
      props.peopleInfiniteScrollProps.fetch(filtersBody)
    }
  })

  const pushUrlStateDebounce = useCallback(
    _.debounce(() => {
      setPushNewSearchState(true)
    }, 500),
    [],
  )

  const paramString = queryString.stringify({
    search: filtersBody.search,
    organizationRoles: filtersBody.organizationRoles,
    showDeactivatedUsers: filtersBody.showDeactivatedUsers,
  })

  const pushUrlState = (): void => {
    window.history.pushState(
      JSON.stringify({}),
      '',
      paramString.length > 0 ? `?${paramString}` : location.pathname,
    )
  }

  const replaceUrlState = (): void => {
    window.history.replaceState(
      JSON.stringify({}),
      '',
      paramString.length > 0 ? `?${paramString}` : location.pathname,
    )
  }

  // restores query params on flyout close
  useEffect((): void => {
    if (!isRightFlyoutOpen && !isLeftFlyoutOpen) {
      replaceUrlState()
    }
  }, [isRightFlyoutOpen, isLeftFlyoutOpen])

  useEffect((): void => {
    if (initialRender) {
      setInitialRender(false)
    } else {
      props.setKeywordFilter &&
        props.setKeywordFilter(props?.stateQueryParams?.search || '')
      props.setOrganizationRoleFilter &&
        props.setOrganizationRoleFilter(
          new Set<string>(props?.stateQueryParams?.organizationRoles),
        )
      props.setHideDeactivatedUsers &&
        props.setHideDeactivatedUsers(
          !!props?.stateQueryParams?.showDeactivatedUsers,
        )
    }
  }, [props?.stateQueryParams])

  useEffect((): void => {
    if (pushNewSearchState) {
      pushUrlState()
      setPushNewSearchState(false)
    }
  }, [pushNewSearchState])

  useEffect((): void => {
    if (!props.initialLoad) {
      debouncedF()
      props.setSelectedFilters &&
        props.setSelectedFilters({
          organizationRoles:
            props.organizationRolesFilter !== undefined
              ? Array.from(props.organizationRolesFilter)
              : [],
          showDeactivatedUsers: !props.hideDeactivatedUsers,
        })
    }
    props.setInitialLoad(false)
  }, [
    props.keywordFilter,
    props.organizationRolesFilter,
    props.hideDeactivatedUsers,
    props.sortBy,
    props.sortDescending,
  ])

  const keywordFilterAnalytics = (val: string): void => {
    switch (calculatedBackgroundLocation.pathname) {
      case peopleMembersUrl():
      default:
        analytics?.track('people members - search org members - typing', {
          screen: peopleMembersUrl(),
          intent: 'search organization members',
          interaction: 'typing',
          search: val,
          results: numberOfOrgMembers,
        })
        break
      case peopleInvitedToOrgUrl():
        analytics?.track('people invited - search invited to org - typing', {
          screen: peopleInvitedToOrgUrl(),
          intent: 'search invited to org',
          interaction: 'typing',
          search: val,
          results: numberOfInvitedPeople,
        })
    }
  }

  useEffect(() => {
    setIsFiltered(
      (props.organizationRolesFilter &&
        props.organizationRolesFilter.size > 0) ||
        (props.keywordFilter !== undefined && props.keywordFilter.length > 0),
    )
  }, [props.organizationRolesFilter, props.keywordFilter])

  useEffect(() => {
    if (
      roleFilterSelected &&
      props.organizationRolesFilter &&
      props.organizationRolesFilter.size > 0
    ) {
      // assumes this functionality is only on the members tab
      analytics?.track('organization members - filter applied', {
        screen: peopleMembersUrl(),
        values: Array.from(props.organizationRolesFilter).map((role) =>
          role.toLowerCase(),
        ),
      })
      setRoleFilterSelected(false)
    }
  }, [roleFilterSelected])

  const resetFilterValues = useMemo(
    () => ({
      keyword: '',
      organizationRolesFilter: new Set<string>(),
      hideDeactivatedUsers: true,
    }),
    [],
  )

  const onFiltersResetClick = (): void => {
    props.setKeywordFilter && props.setKeywordFilter(resetFilterValues.keyword)
    props.setOrganizationRoleFilter &&
      props.setOrganizationRoleFilter(resetFilterValues.organizationRolesFilter)
    props.setHideDeactivatedUsers &&
      props.setHideDeactivatedUsers(resetFilterValues.hideDeactivatedUsers)
    setIsFiltered(false)
    pushUrlStateDebounce()
  }

  const filtersAppliedCount = (): number => {
    let appliedCount = 0
    if (
      !setEquality(
        props.organizationRolesFilter || new Set<string>(),
        resetFilterValues.organizationRolesFilter,
      )
    ) {
      appliedCount++
    }
    if (props.hideDeactivatedUsers !== resetFilterValues.hideDeactivatedUsers) {
      appliedCount++
    }
    return appliedCount
  }

  const handleScroll = useCallback((): void => {
    if (
      !props.peopleInfiniteScrollProps.loading &&
      !props.peopleInfiniteScrollProps.complete
    ) {
      props.peopleInfiniteScrollProps.fetch(
        filtersBody,
        props.peopleInfiniteScrollProps.page !== undefined
          ? props.peopleInfiniteScrollProps.page + 1
          : 0,
      )
    }
  }, [
    props.peopleInfiniteScrollProps.loading,
    props.peopleInfiniteScrollProps.complete,
    props.peopleInfiniteScrollProps.page,
    props.peopleInfiniteScrollProps.fetch,
  ])

  useWindowScrollBottomHandler(handleScroll)

  return (
    <Box
      noPadding
      css={css`
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
      `}
    >
      {calculatedBackgroundLocation.pathname === peopleMembersUrl() ? (
        <FilterSection
          appliedFiltersCount={filtersAppliedCount()}
          topLeftContent={
            <div
              css={css`
                display: flex;
                width: 100%;
                align-items: center;
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  flex-direction: column;
                `)}
              `}
            >
              <div
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )}
                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.highlight,
                      styleConstants.fontFamilies.regular,
                    )};
                    padding: 20px 20px 0px;
                    width: 100%;
                  `)}
                `}
              >
                {sectionCount()}
              </div>
              <div
                css={css`
                  flex-grow: 1;
                  padding-left: 40px;
                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                    display: flex;
                    padding: 18px 20px 20px;
                    width: 100%;
                  `)}
                `}
              >
                <KeywordFilter
                  css={css`
                    ${getBreakpointDown(breakpointSizes.XSM)(css`
                      width: 100%;
                    `)}
                  `}
                  key={'KeywordFilter'}
                  value={props.keywordFilter || ''}
                  onChange={(val: string): void => {
                    keywordFilterAnalytics(val)
                    props.setKeywordFilter && props.setKeywordFilter(val)
                    pushUrlStateDebounce()
                  }}
                  showLabel={false}
                  placeholder={translateSingleString(
                    locale,
                    'Enter a name or email address',
                  )}
                />
              </div>
            </div>
          }
          hideButtonChevron
          filtersFlyoutProps={{
            title: translate('People Filters'),
            onReset: onFiltersResetClick,
            loading: props.peopleInfiniteScrollProps.loading,
            filters: (
              <>
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                  `}
                >
                  <div
                    css={css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    <Trns source={'Role'} />
                  </div>
                  <Checkbox
                    label={<Trns source={'Owner'} />}
                    onClick={(): void => {
                      props.handleOrganizationRoleSelection &&
                        props.handleOrganizationRoleSelection(
                          OrganizationRoles.owner,
                        )
                      handleSelection()
                    }}
                    isSelected={
                      props.organizationRolesFilter
                        ? props.organizationRolesFilter?.has(
                            OrganizationRoles.owner,
                          )
                        : false
                    }
                  />
                  <Checkbox
                    label={<Trns source={'Admin'} />}
                    onClick={(): void => {
                      props.handleOrganizationRoleSelection &&
                        props.handleOrganizationRoleSelection(
                          OrganizationRoles.admin,
                        )
                      handleSelection()
                    }}
                    isSelected={
                      props.organizationRolesFilter
                        ? props.organizationRolesFilter?.has(
                            OrganizationRoles.admin,
                          )
                        : false
                    }
                  />
                  {orgFeatureFlags?.cardManagerRoleEnabled && (
                    <Checkbox
                      label={<Trns source={'Card Manager'} />}
                      onClick={(): void => {
                        props.handleOrganizationRoleSelection &&
                          props.handleOrganizationRoleSelection(
                            OrganizationRoles.cardManager,
                          )
                        handleSelection()
                      }}
                      isSelected={
                        props.organizationRolesFilter
                          ? props.organizationRolesFilter?.has(
                              OrganizationRoles.cardManager,
                            )
                          : false
                      }
                    />
                  )}
                  <Checkbox
                    label={<Trns source={'Bookkeeper'} />}
                    onClick={(): void => {
                      props.handleOrganizationRoleSelection &&
                        props.handleOrganizationRoleSelection(
                          OrganizationRoles.bookkeeper,
                        )
                      handleSelection()
                    }}
                    isSelected={
                      props.organizationRolesFilter
                        ? props.organizationRolesFilter?.has(
                            OrganizationRoles.bookkeeper,
                          )
                        : false
                    }
                  />
                  <Checkbox
                    label={<Trns source={'Member'} />}
                    onClick={(): void => {
                      props.handleOrganizationRoleSelection &&
                        props.handleOrganizationRoleSelection(
                          OrganizationRoles.member,
                        )
                      handleSelection()
                    }}
                    isSelected={
                      props.organizationRolesFilter
                        ? props.organizationRolesFilter?.has(
                            OrganizationRoles.member,
                          )
                        : false
                    }
                  />
                  {orgFeatureFlags?.guestRoleEnabled && (
                    <Checkbox
                      label={<Trns source={'Guest'} />}
                      onClick={(): void => {
                        props.handleOrganizationRoleSelection &&
                          props.handleOrganizationRoleSelection(
                            OrganizationRoles.guest,
                          )
                        handleSelection()
                      }}
                      isSelected={
                        props.organizationRolesFilter
                          ? props.organizationRolesFilter?.has(
                              OrganizationRoles.guest,
                            )
                          : false
                      }
                    />
                  )}
                  <div
                    css={css`
                      margin-top: 32px;
                      display: flex;
                      align-items: flex-start;
                      justify-content: space-between;
                      gap: 8px;
                    `}
                  >
                    <span
                      css={css`
                        ${getFontCss(
                          14,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      <Trns source={'Hide deactivated users'} />
                    </span>
                    <TestId testId={'hideDeactivatedUsers'}>
                      <OnOffSwitch
                        onBgColor={brandColorCta}
                        smallMode
                        defaultOnOffState={props.hideDeactivatedUsers}
                        onChange={(): void => {
                          props.setHideDeactivatedUsers &&
                            props.setHideDeactivatedUsers(
                              !props.hideDeactivatedUsers,
                            )
                          handleSelection()
                        }}
                      />
                    </TestId>
                  </div>
                </div>
              </>
            ),
          }}
        />
      ) : (
        <div
          css={css`
            padding: 15px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              flex-direction: column;
            `)}
          `}
        >
          <span
            css={css`
              padding-left: 15px;
              width: 53%;
              ${getFontCss(
                20,
                themeConstants.colors.highlight,
                styleConstants.fontFamilies.regular,
              )}
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                ${getFontCss(
                  16,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.regular,
                )}
                padding: 20px 20px 0px;
                width: 100%;
              `)}
            `}
          >
            {sectionCount()}
          </span>
          <div
            css={css`
              flex-grow: 1;
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                display: flex;
                padding: 18px 20px 20px;
                width: 100%;
              `)}
            `}
          >
            <KeywordFilter
              css={css`
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  width: 100%;
                `)}
              `}
              key={'KeywordFilter'}
              value={props.keywordFilter || ''}
              onChange={(val: string): void => {
                keywordFilterAnalytics(val)
                props.setKeywordFilter && props.setKeywordFilter(val)
                pushUrlStateDebounce()
              }}
              showLabel={false}
              placeholder={translateSingleString(
                locale,
                'Enter a name or email address',
              )}
            />
          </div>
        </div>
      )}
      {peopleList()}
    </Box>
  )
}

export default PeopleBox
